/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

// components/AdminUnderConstruction.tsx
import { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../utils/apiBaseUrl.ts';
import { getFirestore, doc, getDoc } from 'firebase/firestore';

interface AdminUnderConstructionProps {
  userIsAdmin: boolean;
}

export default function AdminUnderConstruction({ userIsAdmin }: AdminUnderConstructionProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [preventLogin, setPreventLogin] = useState(false);
  const [message, setMessage] = useState('');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [updatedBy, setUpdatedBy] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadUnderConstructionStatus();
  }, []);

  const loadUnderConstructionStatus = async () => {
    try {
      const db = getFirestore();
      const configRef = doc(db, 'system_config', 'under_construction');
      const configDoc = await getDoc(configRef);

      if (configDoc.exists()) {
        const data = configDoc.data();
        setPreventLogin(data.prevent_login || false);
        setMessage(data.message || 'We are currently making some updates, but will be back online soon.');
        if (data.lastUpdated) {
          setLastUpdated(data.lastUpdated.toDate());
        }
        setUpdatedBy(data.updatedBy || null);
      } else {
        // Set defaults if document doesn't exist
        setMessage('We are currently making some updates, but will be back online soon.');
      }
    } catch (err) {
      console.error('Error loading under construction status:', err);
    }
  };

  const handleUpdateConstruction = async () => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const auth = (await import('firebase/auth')).getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        throw new Error('Not authenticated');
      }

      const idToken = await user.getIdToken();
      
      // Call Netlify function to update construction mode
      const response = await fetch(`${API_BASE_URL}/.netlify/functions/admin-under-construction`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prevent_login: preventLogin,
          message: message.trim() || 'We are currently making some updates, but will be back online soon.'
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update construction mode');
      }

      setSuccess(
        preventLogin 
          ? 'Construction mode enabled! Non-admin users will see the maintenance screen.' 
          : 'Construction mode disabled. All users can now access the portal.'
      );
      setShowConfirm(false);
      
      // Wait for Firestore to propagate the changes before reloading
      console.log('⏳ Waiting for Firestore to sync...');
      await new Promise(resolve => setTimeout(resolve, 1500));
      console.log('🔄 Reloading construction status...');
      await loadUnderConstructionStatus();
      console.log('✅ Status reloaded');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const formatTimestamp = (date: Date | null) => {
    if (!date) return 'Never';
    return date.toLocaleString('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  };

  if (!userIsAdmin) {
    return null;
  }

  return (
    <div>
      <h3>Under Construction Mode</h3>
      <p>
        Enable maintenance mode to prevent non-admin users from accessing the portal. 
        Customize the message users will see.
      </p>

      {/* Current Status with Message Box */}
      <div style={{ 
        padding: '16px', 
        backgroundColor: preventLogin ? '#fef2f2' : '#f0fdf4', 
        border: `1px solid ${preventLogin ? '#fca5a5' : '#86efac'}`,
        borderRadius: '8px',
        marginBottom: '16px'
      }}>
        <div style={{ marginBottom: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ fontWeight: '600' }}>Status:</span>
            <span style={{ 
              padding: '2px 8px', 
              borderRadius: '4px',
              backgroundColor: preventLogin ? '#dc2626' : '#16a34a',
              color: 'white',
              fontSize: '14px'
            }}>
              {preventLogin ? '🚧 ACTIVE' : '✅ INACTIVE'}
            </span>
          </div>
          {lastUpdated && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', marginBottom: '4px' }}>
              <span style={{ color: '#6b7280' }}>Last Updated:</span>
              <span style={{ color: '#111827' }}>{formatTimestamp(lastUpdated)}</span>
            </div>
          )}
          {updatedBy && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
              <span style={{ color: '#6b7280' }}>Updated By:</span>
              <span style={{ color: '#111827' }}>{updatedBy}</span>
            </div>
          )}
        </div>
        
        {/* Message box inside status */}
        <div>
          <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', fontSize: '14px' }}>
            Custom Message:
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="We are currently making some updates, but will be back online soon."
            rows={3}
            disabled={isLoading}
            style={{
              width: 'calc(100% - 25px)',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              fontSize: '14px',
              fontFamily: 'inherit',
              backgroundColor: 'white',
              resize: 'vertical'
            }}
          />
          <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
            This message will be displayed to non-admin users when construction mode is enabled.
          </p>
        </div>
      </div>

      {/* Controls */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px',
          marginBottom: '12px',
          cursor: 'pointer'
        }}>
          <input
            type="checkbox"
            checked={preventLogin}
            onChange={(e) => setPreventLogin(e.target.checked)}
            disabled={isLoading}
            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
          />
          <span style={{ fontWeight: '600' }}>Enable Construction Mode (Block Non-Admin Users)</span>
        </label>
      </div>

      {!showConfirm ? (
        <button
          onClick={() => setShowConfirm(true)}
          style={{
            padding: '10px 20px',
            backgroundColor: preventLogin ? '#dc2626' : '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '600'
          }}
        >
          {preventLogin ? '🚧 Update Construction Mode' : '✅ Save Settings'}
        </button>
      ) : (
        <div style={{ padding: '16px', backgroundColor: '#fef3c7', border: '1px solid #fbbf24', borderRadius: '8px' }}>
          <p style={{ fontWeight: '600', marginBottom: '8px' }}>⚠️ Confirm Changes</p>
          <p style={{ fontSize: '14px', marginBottom: '16px' }}>
            {preventLogin 
              ? 'This will BLOCK all non-admin users from accessing the portal and show them the maintenance message.'
              : 'This will allow all users to access the portal normally.'}
          </p>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={handleUpdateConstruction}
              disabled={isLoading}
              style={{
                padding: '8px 16px',
                backgroundColor: preventLogin ? '#dc2626' : '#16a34a',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                opacity: isLoading ? 0.6 : 1
              }}
            >
              {isLoading ? 'Processing...' : 'Yes, Update'}
            </button>
            <button
              onClick={() => {
                setShowConfirm(false);
                loadUnderConstructionStatus(); // Reset to saved values
              }}
              disabled={isLoading}
              style={{
                padding: '8px 16px',
                backgroundColor: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                opacity: isLoading ? 0.6 : 1
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {success && (
        <div style={{ 
          marginTop: '16px', 
          padding: '12px', 
          backgroundColor: '#d1fae5', 
          border: '1px solid #34d399',
          borderRadius: '8px'
        }}>
          <p style={{ fontWeight: '600', marginBottom: '4px' }}>✅ Success</p>
          <p style={{ fontSize: '14px' }}>{success}</p>
        </div>
      )}

      {error && (
        <div style={{ 
          marginTop: '16px', 
          padding: '12px', 
          backgroundColor: '#fee2e2', 
          border: '1px solid #f87171',
          borderRadius: '8px'
        }}>
          <p style={{ fontWeight: '600', marginBottom: '4px' }}>❌ Error</p>
          <p style={{ fontSize: '14px' }}>{error}</p>
        </div>
      )}
    </div>
  );
}